import { ObjectType } from '@/core/data/localization/localization.type';
import { RatingType } from '@/core/data/rating/rating.type';
import { PlayerStatus, PlayerTitle, PlayerType, RatingDisplayMode } from '@/core/data/user/user.type';
import { localizeType } from '@/core/gambits';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';

const DOT_THRESHOLDS: { max: number; dots: number }[] = [
  { max: 475, dots: 1 },
  { max: 1150, dots: 2 },
  { max: Infinity, dots: 3 },
];

const DOT_TO_RATING: Record<number, number> = { 1: 150, 2: 800, 3: 1500 };

export default class PlayerHelper {
  static getPlayerTitleLocale(title: PlayerTitle, fullName: boolean): string {
    const playerTitle = fullName ? `${title.toLowerCase()}.full` : title.toLowerCase();
    return localizeType(ObjectType.PLAYER_TITLE, playerTitle);
  }

  static getPlayerTypeLocale(type: PlayerType): string {
    return localizeType(ObjectType.PLAYER, type);
  }

  static getPlayerStatusLocale(status?: PlayerStatus): string {
    return `player.card.status.${status || PlayerStatus.OFFLINE}`;
  }

  static getPlayerRatingIcon(type: RatingType): GamIconName | undefined {
    switch (type) {
      case RatingType.GAMBIT:
        return GamIconName.GAMBIT;
      case RatingType.CHESSCOM:
        return GamIconName.CHESS_COM;
      case RatingType.FIDE:
        return GamIconName.FIDE;
      case RatingType.LICHESS:
        return GamIconName.LICHESS;
      default:
        return undefined;
    }
  }

  static ratingToDots(rating: number): number {
    for (const t of DOT_THRESHOLDS) {
      if (rating <= t.max) return t.dots;
    }
    return 3;
  }

  static dotsToRating(dots: number): number {
    return DOT_TO_RATING[dots] ?? 800;
  }

  static isDotsMode(mode?: RatingDisplayMode): boolean {
    return mode === RatingDisplayMode.DOTS;
  }

  /* One person keeps their own display. A group is always a number, because
     squares cannot show a range. */
  static groupRatingPresentation(
    players: Array<{ rating?: number | null; ratingDisplayMode?: string | null }>,
  ): { label: string } | { dotsRating: number } | null {
    const rated = players.filter((player) => typeof player.rating === 'number' && player.rating > 0);

    if (!rated.length) return null;

    if (rated.length === 1) {
      const rating = rated[0].rating as number;
      if (rated[0].ratingDisplayMode === RatingDisplayMode.DOTS) return { dotsRating: rating };
      return { label: String(rating) };
    }

    const values = rated.map((player) => player.rating as number);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { label: min === max ? String(min) : `${min} - ${max}` };
  }
}
